import * as React from "react"
import { createContext, useCallback, useContext, useEffect, useRef } from "react"
import { AppState, View, ViewStyle } from "react-native"

import { useNavigationContainerRef } from "expo-router"

import { useAuth } from "src/context/AuthContext"
import { useCurrentConversation } from "src/queries/useConversation"

const REPORT_INTERVAL_MS = 10000 // report touch events every 10 seconds
const VIDEO_ACTIVITY_INTERVAL_MS = 10000 // report video activity every 10 seconds

// Create a context for website video activity
interface WebsiteVideoActivityContextType {
  setWebsiteVideoActivity: (activity: boolean) => void
}

const WebsiteVideoActivityContext = createContext<WebsiteVideoActivityContextType | null>(null)

export const useWebsiteVideoActivity = () => {
  const context = useContext(WebsiteVideoActivityContext)
  if (!context) {
    throw new Error("useWebsiteVideoActivity must be used within ActivityTrackerWrapper")
  }
  return context
}

interface ActivityTrackerWrapperProps {
  children: React.ReactNode
}

export default function ActivityTrackerWrapper({ children }: ActivityTrackerWrapperProps) {
  const { api, isAuthenticated } = useAuth()
  const navigationRef = useNavigationContainerRef()
  const { data: currentConversation } = useCurrentConversation()

  const conversationId = currentConversation?.id
  const lastReportTime = useRef(0)
  const lastVideoReportTime = useRef(0)
  const videoActivityInterval = useRef<number | null>(null)
  // State for tracking video activity from video-player route
  const [websiteVideoActivity, setWebsiteVideoActivity] = React.useState<boolean>(false)

  const registerActivity = useCallback(async () => {
    const now = Date.now()

    if (now - lastReportTime.current >= REPORT_INTERVAL_MS) {
      try {
        const response = await api.reportActivity(now, "touchEvent", "touchEvent", conversationId)
        if (response.kind === "ok") {
          lastReportTime.current = now
        } else {
          console.warn("[ActivityTracker] Failed to report activity:", response)
        }
      } catch (error) {
        console.error("[ActivityTracker] Error reporting activity:", error)
      }
    } else {
      // console.log("[ActivityTracker] Skipping touch report - too soon since last report")
    }
  }, [api, conversationId])

  const registerVideoActivity = useCallback(async () => {
    const now = Date.now()

    if (now - lastVideoReportTime.current >= VIDEO_ACTIVITY_INTERVAL_MS) {
      try {
        const response = await api.reportActivity(
          now,
          "videoWatching",
          "videoWatching",
          conversationId,
        )
        if (response.kind === "ok") {
          lastVideoReportTime.current = now
          console.log("[ActivityTracker] Reported video activity")
        } else {
          console.warn("[ActivityTracker] Failed to report video activity:", response)
        }
      } catch (error) {
        console.error("[ActivityTracker] Error reporting video activity:", error)
      }
    } else {
      // console.log("[ActivityTracker] Skipping video report - too soon since last report")
    }
  }, [api, conversationId])

  // Track video watching activity when websiteVideoActivity is set by video-player or web-browser routes
  useEffect(() => {
    if (!isAuthenticated || !websiteVideoActivity) {
      // Clear any existing video activity interval
      if (videoActivityInterval.current) {
        clearInterval(videoActivityInterval.current)
        videoActivityInterval.current = null
      }
      return
    }

    // Report immediately when video starts
    registerVideoActivity()

    // Set up interval to report activity while video is active
    videoActivityInterval.current = setInterval(() => {
      registerVideoActivity()
    }, VIDEO_ACTIVITY_INTERVAL_MS)

    return () => {
      if (videoActivityInterval.current) {
        clearInterval(videoActivityInterval.current)
        videoActivityInterval.current = null
      }
    }
  }, [isAuthenticated, registerVideoActivity, websiteVideoActivity])

  // Report activity when the app returns to the foreground from background
  useEffect(() => {
    if (!isAuthenticated) return

    const subscription = AppState.addEventListener("change", (nextAppState) => {
      if (nextAppState === "active") {
        const now = Date.now()
        api.reportActivity(now, "foreground", "foreground", conversationId).catch((error) => {
          console.warn("[ActivityTracker] Failed to report foreground activity:", error)
        })
      }
    })

    return () => subscription.remove()
  }, [api, conversationId, isAuthenticated])

  // Listen to navigation events
  useEffect(() => {
    if (!navigationRef?.current) {
      return
    }

    const unsubscribe = navigationRef.addListener("state", () => {
      registerActivity()
    })

    return unsubscribe
  }, [navigationRef, registerActivity])

  useEffect(() => {
    return () => {
      if (videoActivityInterval.current) {
        clearInterval(videoActivityInterval.current)
      }
    }
  }, [])

  // Don't track if user is not authenticated
  if (!isAuthenticated) {
    return <>{children}</>
  }

  const contextValue = {
    setWebsiteVideoActivity,
  }

  return (
    <WebsiteVideoActivityContext.Provider value={contextValue}>
      <View style={$container} onTouchStart={registerActivity}>
        {children}
      </View>
    </WebsiteVideoActivityContext.Provider>
  )
}

const $container: ViewStyle = {
  flex: 1,
}
